import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { quizzesApi } from '@/api';
import type { QuizListParams } from '@/api/quizzes';
import { extractApiError, extractApiErrorWithTierNumbers } from '@/api/errorStatus';
import type { QuizzesState, Quiz } from '@/types';
import { preserveOwnerName } from './preserveOwnerName';
import { switchActiveOrgThunk } from './organizationsSlice';
import { switchActiveProjectThunk, clearActiveProjectThunk } from './projectsSlice';
import { rejectedApiError } from './rejectedApiError';

const initialState: QuizzesState = {
  quizzes: [],
  total: 0,
  isLoading: false,
  error: null,
  limit: 20,
  offset: 0,
};

/**
 * WEB-745: fetchQuizzesThunk now accepts pagination/search params. Backward
 * compatible with the bare-string call (`fetchQuizzesThunk(projectId)`) used
 * throughout the existing codebase/tests — that form fetches with backend
 * defaults (limit=20, offset=0).
 */
export type FetchQuizzesArg = string | ({ projectId: string } & QuizListParams);

function normalizeFetchArg(arg: FetchQuizzesArg): { projectId: string } & QuizListParams {
  return typeof arg === 'string' ? { projectId: arg } : arg;
}

export const fetchQuizzesThunk = createAsyncThunk(
  'quizzes/fetchAll',
  async (arg: FetchQuizzesArg, { rejectWithValue }) => {
    const { projectId, ...params } = normalizeFetchArg(arg);
    try {
      const response = await quizzesApi.list(projectId, params);
      return response.data as { quizzes: Quiz[]; total: number };
    } catch (error: unknown) {
      if (error instanceof Error) {
        return rejectWithValue(extractApiError(error));
      }
      return rejectWithValue('Failed to fetch quizzes');
    }
  }
);

export const createQuizThunk = createAsyncThunk(
  'quizzes/create',
  async (
    { projectId, data }: { projectId: string; data: Record<string, unknown> },
    { rejectWithValue }
  ) => {
    try {
      const response = await quizzesApi.create(projectId, data);
      return response.data as Quiz;
    } catch (error: unknown) {
      // WEB-391: preserve the HTTP status (the interceptor attaches it) so the
      // caller can detect a 402 tier-limit and open the PricingModal upsell —
      // a bare message string lost the status and the upsell never fired.
      // WEB-1955: разбор отказа — одной дверью (`extractApiErrorWithTierNumbers`,
      // api/errorStatus.ts). Ручная сборка пейлоада теряла `reasonCode` —
      // причину денежного 402, по которой окно тарифа называет, что именно
      // кончилось, — и клала `code: null` вместо «кода нет», отчего
      // страховочная ветка «402 без кода» в utils/tierLimit.ts была
      // недостижима.
      return rejectWithValue(extractApiErrorWithTierNumbers(error, 'Failed to create quiz'));
    }
  }
);

export const duplicateQuizThunk = createAsyncThunk(
  'quizzes/duplicate',
  async (
    { projectId, id }: { projectId: string; id: string },
    { rejectWithValue }
  ) => {
    try {
      const response = await quizzesApi.duplicate(projectId, id);
      return response.data as Quiz;
    } catch (error: unknown) {
      // WEB-911: дублирование теперь может отказать по лимиту тарифа (402
      // TIER_LIMIT). Плоская строка убивала статус и код — страница не могла
      // отличить отказ по тарифу от поломки (шрам WEB-391).
      return rejectWithValue(extractApiErrorWithTierNumbers(error, 'Failed to duplicate quiz'));
    }
  }
);

export const deleteQuizThunk = createAsyncThunk(
  'quizzes/delete',
  async (
    { projectId, id }: { projectId: string; id: string },
    { rejectWithValue }
  ) => {
    try {
      await quizzesApi.delete(projectId, id);
      return id;
    } catch (error: unknown) {
      if (error instanceof Error) {
        return rejectWithValue(extractApiError(error));
      }
      return rejectWithValue('Failed to delete quiz');
    }
  }
);

export const publishQuizThunk = createAsyncThunk(
  'quizzes/publish',
  async (
    { projectId, id }: { projectId: string; id: string },
    { rejectWithValue }
  ) => {
    try {
      const response = await quizzesApi.publish(projectId, id);
      return response.data as Quiz;
    } catch (error: unknown) {
      // QA 2026-07-17: сохранить серверное сообщение (NO_DOMAIN_BOUND и т.п.) —
      // см. paywallsSlice. Иначе таблица квизов показывает «Unexpected error».
      return rejectWithValue(extractApiError(error));
    }
  }
);

export const unpublishQuizThunk = createAsyncThunk(
  'quizzes/unpublish',
  async (
    { projectId, id }: { projectId: string; id: string },
    { rejectWithValue }
  ) => {
    try {
      const response = await quizzesApi.unpublish(projectId, id);
      return response.data as Quiz;
    } catch (error: unknown) {
      return rejectWithValue(extractApiError(error));
    }
  }
);

/**
 * РЕВИЗИЯ ПАЧКИ 23 (находка 3). Перенос и копия квиза отвергали ГОЛОЙ
 * СТРОКОЙ — а страница (Quizzes.tsx, handleMoveCopy) с WEB-911 разбирает
 * отказ по лимиту тарифа через `asTierLimitRejection`, который судит по
 * `status`/`code`. На строке он всегда возвращал null: ветка окна покупки
 * тарифа была написана, но недостижима — человек, упёршийся в лимит при
 * копировании квиза, читал техническую жалобу вместо выхода.
 *
 * Числа `usage`/`limit` сохраняются рядом со статусом (их кладёт перехватчик,
 * client.ts) — иначе окно не сможет сказать «использовано N из M». Разбор
 * общий с воронками — `extractApiErrorWithTierNumbers` (`api/errorStatus.ts`),
 * второй редакции правила по слайсам не заводится (WEB-1844).
 */
export const moveQuizThunk = createAsyncThunk(
  'quizzes/move',
  async (
    { projectId, id, targetProjectId }: { projectId: string; id: string; targetProjectId: string },
    { rejectWithValue }
  ) => {
    try {
      await quizzesApi.move(projectId, id, targetProjectId);
      return id;
    } catch (error: unknown) {
      return rejectWithValue(extractApiErrorWithTierNumbers(error, 'Failed to move quiz'));
    }
  }
);

// WEB-386 Z3-F3: inline rename in the quiz list — reuses the existing
// PATCH /projects/:projectId/quizzes/:id endpoint (quizzesApi.update).
export const updateQuizThunk = createAsyncThunk(
  'quizzes/update',
  async (
    { projectId, id, data }: { projectId: string; id: string; data: Record<string, unknown> },
    { rejectWithValue }
  ) => {
    try {
      const response = await quizzesApi.update(projectId, id, data);
      return response.data as Quiz;
    } catch (error: unknown) {
      if (error instanceof Error) {
        return rejectWithValue(extractApiError(error));
      }
      return rejectWithValue('Failed to update quiz');
    }
  }
);

export const copyQuizThunk = createAsyncThunk(
  'quizzes/copy',
  async (
    { projectId, id, targetProjectId }: { projectId: string; id: string; targetProjectId: string },
    { rejectWithValue }
  ) => {
    try {
      const response = await quizzesApi.copy(projectId, id, targetProjectId);
      return response.data as Quiz;
    } catch (error: unknown) {
      return rejectWithValue(extractApiErrorWithTierNumbers(error, 'Failed to copy quiz'));
    }
  }
);

const quizzesSlice = createSlice({
  name: 'quizzes',
  initialState,
  reducers: {
    clearError(state) {
      return { ...state, error: null };
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchQuizzesThunk.pending, (state, action) => ({
        ...state,
        isLoading: true,
        error: null,
        // Ф3 (ревизия десятки 3, 15.08): requestId-гард — см. QuizzesState.latestRequestId.
        latestRequestId: action.meta?.requestId ?? null,
      }))
      .addCase(fetchQuizzesThunk.fulfilled, (state, action) => {
        // Ф3: a stale (superseded) response landing late must not overwrite
        // the fresher list already on screen. state.latestRequestId is only
        // ever null/undefined when no pending was observed for this state
        // (older preloadedState fixtures/tests) — treated as "unknown",
        // backward compatible with applying unconditionally.
        if (state.latestRequestId != null && action.meta.requestId !== state.latestRequestId) {
          return state;
        }
        // WEB-745: the store holds exactly the fetched page — record the
        // limit/offset that produced it so the list page can drive antd
        // Table's controlled pagination from the store.
        const { limit, offset } = normalizeFetchArg(action.meta.arg);
        return {
          ...state,
          isLoading: false,
          quizzes: action.payload.quizzes,
          total: action.payload.total,
          limit: limit ?? 20,
          offset: offset ?? 0,
        };
      })
      // WEB-1337 P2: a failed refetch must not leave the PREVIOUS successful
      // list/total on screen next to the error banner — that reads as valid
      // current data (possibly for a stale filter/page/context) instead of
      // "we don't actually know the current list".
      .addCase(fetchQuizzesThunk.rejected, (state, action) => {
        // Ф3: same out-of-order guard as fulfilled above.
        if (state.latestRequestId != null && action.meta.requestId !== state.latestRequestId) {
          return state;
        }
        return {
          ...state,
          isLoading: false,
          error: rejectedApiError(action),
          quizzes: initialState.quizzes,
          total: initialState.total,
        };
      })
      .addCase(createQuizThunk.fulfilled, (state, action) => ({
        ...state,
        quizzes: [...state.quizzes, action.payload],
        total: state.total + 1,
      }))
      .addCase(duplicateQuizThunk.fulfilled, (state, action) => ({
        ...state,
        quizzes: [...state.quizzes, action.payload],
        total: state.total + 1,
      }))
      .addCase(deleteQuizThunk.fulfilled, (state, action) => ({
        ...state,
        quizzes: state.quizzes.filter((q) => q.id !== action.payload),
        total: state.total - 1,
      }))
      .addCase(publishQuizThunk.fulfilled, (state, action) => ({
        ...state,
        quizzes: state.quizzes.map((q) =>
          q.id === action.payload.id ? preserveOwnerName(q, action.payload) : q
        ),
      }))
      .addCase(unpublishQuizThunk.fulfilled, (state, action) => ({
        ...state,
        quizzes: state.quizzes.map((q) =>
          q.id === action.payload.id ? preserveOwnerName(q, action.payload) : q
        ),
      }))
      // WEB-4114 (сверка вида 31.10, К2 № 2): отказ снятия с публикации НЕ
      // пишет `error` — это поле отказа загрузки списка, и страница по нему
      // заменяла всю таблицу на «Не удалось загрузить …». Отказ снятия
      // показывает всплывашка страницы (WEB-1370 п.1, `.unwrap()`), список
      // остаётся.
      .addCase(updateQuizThunk.fulfilled, (state, action) => ({
        ...state,
        quizzes: state.quizzes.map((q) =>
          q.id === action.payload.id ? preserveOwnerName(q, action.payload) : q
        ),
      }))
      .addCase(moveQuizThunk.fulfilled, (state, action) => ({
        ...state,
        quizzes: state.quizzes.filter((q) => q.id !== action.payload),
        total: state.total - 1,
      }))
      // WEB-1058: см. funnelsSlice — квизы, как и воронки, живут внутри
      // проекта организации; переключение активной организации оставляло
      // квизы прежней организации в сторе (видны в Контент → Квизы до F5).
      .addCase(switchActiveOrgThunk.fulfilled, (state) => ({
        ...state,
        quizzes: initialState.quizzes,
        total: initialState.total,
        error: initialState.error,
        limit: initialState.limit,
        offset: initialState.offset,
      }))
      // WEB-1337 P1: mirror of switchActiveOrgThunk.fulfilled above, for
      // switching PROJECT within the same org (or entering org-mode) — the
      // quiz list is scoped to a project too.
      .addCase(switchActiveProjectThunk.fulfilled, (state) => ({
        ...state,
        quizzes: initialState.quizzes,
        total: initialState.total,
        error: initialState.error,
        limit: initialState.limit,
        offset: initialState.offset,
      }))
      .addCase(clearActiveProjectThunk.fulfilled, (state) => ({
        ...state,
        quizzes: initialState.quizzes,
        total: initialState.total,
        error: initialState.error,
        limit: initialState.limit,
        offset: initialState.offset,
      }));
    // copyQuizThunk: no state change — the copy lands in another project
  },
});

export const { clearError } = quizzesSlice.actions;
export default quizzesSlice.reducer;
